/**
 * i18n W2 (2026-05-15) — BE 에러 코드 상수 + 비교 헬퍼.
 *
 * 배경: FE 가 한글 메시지 문자열로 분기하면 locale=en 환경에서 깨짐.
 *      BE 가 응답에 `errorName` (enum.name(), 예: "NOT_ACTIVE_USER") 을 실어보냄.
 *      FE 는 이 식별자로 분기.
 *
 * 하위호환: BE 구버전(errorName 없음) 응답이 한동안 공존 가능 →
 *          모든 비교 헬퍼는 errorName 우선 + legacy 한글 message 동시 매칭.
 */

// FE 내부 sentinel — axios interceptor 가 토큰 재발급 실패 시 던지는 신호.
// BE 응답이 아니라 FE 자체 throw 이므로 errorName 도 FE 가 부여.
export const FE_ERROR_TOKEN_REFRESH_FAILED = "TOKEN_REFRESH_FAILED";
const LEGACY_TOKEN_REFRESH_FAILED_MSG = "토큰 재발급 실패";

// BE enum.name() — BaseResponseStatus.java 와 동기화 필요.
export const ERR_NOT_ACTIVE_USER = "NOT_ACTIVE_USER";
const LEGACY_NOT_ACTIVE_USER_MSG = "로그인 절차를 완료 하지 않은 유저 입니다.";

export const ERR_DELETED_USER = "DELETED_USER";
const LEGACY_DELETED_USER_MSG = "이미 탈퇴한 회원 입니다.";

export const ERR_PASSWORD_ENCRYPTION = "PASSWORD_ENCRYPTION_ERROR";
const LEGACY_PASSWORD_ENCRYPTION_MSG = "비밀번호 암호화에 실패하였습니다.";

export const ERR_OVER_CARDDECK_CAPACITY = "OVER_CARDDECK_CAPACITY";
const LEGACY_OVER_CARDDECK_CAPACITY_MSG = "단기기억소가 가득찼어요! 스테이지 먼저 비우러가기";

/** 토큰 재발급 실패 sentinel 인지 — FE throw 한 Error 객체 검사. */
export function isTokenRefreshFailed(e: any): boolean {
  if (!e) return false;
  if (e.errorName === FE_ERROR_TOKEN_REFRESH_FAILED) return true;
  if (e.message === LEGACY_TOKEN_REFRESH_FAILED_MSG) return true;
  return false;
}

/** BE 응답 또는 throw 된 에러가 특정 errorName 인지 (legacy 한글 메시지 동시 매칭). */
export function hasErrorCode(
  source: any,
  errorName: string,
  legacyMessage?: string,
  matchSubstring = false,
): boolean {
  if (!source) return false;
  if (source.errorName === errorName) return true;
  if (legacyMessage) {
    const msg = source.message;
    if (typeof msg !== "string") return false;
    return matchSubstring ? msg.includes(legacyMessage) : msg === legacyMessage;
  }
  return false;
}

/** BE 응답 data 객체에서 NOT_ACTIVE_USER 인지 — 기존 `message.includes(...)` 대응으로 substring 매칭. */
export function isNotActiveUser(source: any): boolean {
  return hasErrorCode(source, ERR_NOT_ACTIVE_USER, LEGACY_NOT_ACTIVE_USER_MSG, true);
}

export function isDeletedUser(source: any): boolean {
  return hasErrorCode(source, ERR_DELETED_USER, LEGACY_DELETED_USER_MSG, false);
}

export function isPasswordEncryptionError(source: any): boolean {
  return hasErrorCode(source, ERR_PASSWORD_ENCRYPTION, LEGACY_PASSWORD_ENCRYPTION_MSG, true);
}

export function isOverCarddeckCapacity(source: any): boolean {
  return hasErrorCode(source, ERR_OVER_CARDDECK_CAPACITY, LEGACY_OVER_CARDDECK_CAPACITY_MSG, false);
}

/** 토큰 재발급 실패 Error 객체 생성 — axios interceptor 에서 사용. */
export function createTokenRefreshFailedError(): Error {
  const err = new Error(LEGACY_TOKEN_REFRESH_FAILED_MSG) as Error & { errorName: string };
  err.errorName = FE_ERROR_TOKEN_REFRESH_FAILED;
  return err;
}
